/**
 * Smooth Scrolling with Lenis
 * Provides smooth scrolling functionality with configurable options
 */

import Lenis from "lenis";

class SmoothScroll {
  constructor() {
    this.lenis = null;
    this.isInitialized = false;
    this.proxySetup = false; // Track if ScrollTrigger proxy has been set up
    this.config = {
      duration: 2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // https://www.desmos.com/calculator/brs54l4xou
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    };

    this.init();
  }

  init() {
    // Only initialize on desktop devices
    if (this.isMobile()) {
      return;
    }

    try {
      this.lenis = new Lenis(this.config);

      // RAF loop
      this.raf(0);

      // Integrate with GSAP if available
      this.integrateWithGSAP();

      // Add event listeners
      this.addEventListeners();

      this.isInitialized = true;

      if (this.isDebugEnabled()) {
        console.log("[SOJ] Lenis smooth scrolling initialized");
      }
    } catch (error) {
      if (this.isDebugEnabled()) {
        console.error("[SOJ] Failed to initialize Lenis:", error);
      }
    }
  }

  /**
   * Check if debug mode is enabled
   * @returns {boolean} True if debug is enabled
   */
  isDebugEnabled() {
    return typeof sojTheme !== "undefined" && sojTheme.debug;
  }

  raf(time) {
    if (this.lenis) {
      this.lenis.raf(time);
    }
    // Only use RAF if GSAP is not available (GSAP ticker handles it otherwise)
    if (typeof gsap === "undefined") {
      requestAnimationFrame(this.raf.bind(this));
    }
  }

  integrateWithGSAP() {
    // Check if GSAP and ScrollTrigger are available
    if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && this.lenis) {
      this.setupScrollTriggerProxy();
    } else {
      // If GSAP isn't ready yet, try again after a short delay
      setTimeout(() => {
        if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && this.lenis) {
          this.setupScrollTriggerProxy();
        }
      }, 100);
    }
  }

  setupScrollTriggerProxy() {
    if (!this.lenis || typeof ScrollTrigger === "undefined") {
      return;
    }

    // Prevent duplicate setup
    if (this.proxySetup) {
      return;
    }

    const lenis = this.lenis; // Capture reference for use in proxy functions
    
    // Configure ScrollTrigger to use Lenis as the scroller proxy
    // This is critical to prevent conflicts between Lenis smooth scroll and ScrollTrigger
    ScrollTrigger.scrollerProxy(window, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return lenis.scroll;
      },
      scrollLeft(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true, horizontal: true });
        }
        return 0;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight,
        };
      },
      pinType: document.body.style.transform ? "transform" : "fixed"
    });

    // Update ScrollTrigger on Lenis scroll
    lenis.on("scroll", ScrollTrigger.update);

    // Use GSAP ticker for RAF (this replaces the separate RAF loop)
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    this.proxySetup = true;

    if (this.isDebugEnabled()) {
      console.log("[SOJ] ScrollTrigger proxy configured for Lenis");
    }
  }

  addEventListeners() {
    // NOTE: For this project we do NOT pause Lenis for the mobile menu,
    // because the menu itself contains a scrollable area and we want that
    // to remain scrollable even when the drawer is open.
    // If needed in future, custom logic can be added here.
    document.addEventListener("mobileMenuOpen", () => {
      // Intentionally left blank to avoid lenis.stop()
    });

    document.addEventListener("mobileMenuClose", () => {
      // Intentionally left blank to avoid lenis.start()
    });

    // Pause scrolling when modals are open
    document.addEventListener("modalOpen", () => {
      if (this.lenis) {
        this.lenis.stop();
      }
    });

    document.addEventListener("modalClose", () => {
      if (this.lenis) {
        this.lenis.start();
      }
    });

    // Pause Lenis when user interacts with iframes to prevent scroll interference
    this.setupIframeHandling();
  }

  setupIframeHandling() {
    if (!this.lenis) {
      return;
    }

    // Handle iframe mouse enter/leave to pause/resume Lenis
    document.addEventListener("mouseenter", (event) => {
      if (event.target.tagName === "IFRAME") {
        if (this.lenis) {
          this.lenis.stop();
        }
      }
    }, true);

    document.addEventListener("mouseleave", (event) => {
      if (event.target.tagName === "IFRAME") {
        if (this.lenis) {
          this.lenis.start();
        }
      }
    }, true);

    // Also handle wheel events on iframes to prevent Lenis from interfering
    document.addEventListener("wheel", (event) => {
      const iframe = event.target.closest("iframe");
      if (iframe) {
        // User is scrolling inside an iframe - let it handle the scroll
        // Lenis will be paused via mouseenter, but ensure it stays paused
        if (this.lenis) {
          this.lenis.stop();
        }
      }
    }, { passive: true, capture: true });
  }

  // Public methods
  scrollTo(target, options = {}) {
    if (this.lenis) {
      this.lenis.scrollTo(target, options);
    }
  }

  scrollToTop() {
    if (this.lenis) {
      this.lenis.scrollTo(0, { duration: 1.5 });
    }
  }

  scrollToElement(selector, options = {}) {
    const element = document.querySelector(selector);
    if (element && this.lenis) {
      this.lenis.scrollTo(element, {
        duration: 1.5,
        offset: -100, // Offset for fixed header
        ...options,
      });
    }
  }

  pause() {
    if (this.lenis) {
      this.lenis.stop();
    }
  }

  resume() {
    if (this.lenis) {
      this.lenis.start();
    }
  }

  destroy() {
    if (this.lenis) {
      this.lenis.destroy();
      this.lenis = null;
      this.isInitialized = false;
    }
  }

  // Utility methods
  isMobile() {
    return window.innerWidth <= 768;
  }

  // Get scroll progress (0 to 1)
  getScrollProgress() {
    if (this.lenis) {
      return this.lenis.progress;
    }
    return 0;
  }

  // Get current scroll position
  getScrollPosition() {
    if (this.lenis) {
      return this.lenis.scroll;
    }
    return window.pageYOffset;
  }

  // Get scroll direction
  getScrollDirection() {
    if (this.lenis) {
      return this.lenis.direction;
    }
    return 1;
  }
}

// Export the class (not an instance) for use in other modules
export default SmoothScroll;
